import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  Paper,
  IconButton,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import { useNotification } from '../contexts/NotificationContext';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import DashboardAccessChip from './DashboardAccessChip';
import { isDashboardReadOnly, isGrantedDashboard, dashboardAccessMeta } from '../utils/dashboardAccess';

const DashboardList = ({ 
  open, 
  onClose, 
  dashboards, 
  onLoadDashboard, 
  onNewDashboard,
  onDeleteDashboard,
  onDashboardDuplicated,
  onCopyDashboardLink,
  isAuthenticated, 
  authToken, 
  managerId, 
  adminId 
}) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const [search, setSearch] = useState('');

  // Cada apertura del diálogo muestra la lista completa.
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  // Helper para obtener headers de autenticación
  const getAuthHeaders = () => {
    const headers = {
      'Authorization': authToken
    };
    if (adminId) {
      headers['X-Admin-Id'] = adminId;
    } else if (managerId) {
      headers['X-Manager-Id'] = managerId;
    }
    return headers;
  };

  const handleLoad = (dashboard) => {
    onLoadDashboard(dashboard);
    onClose();
  };

  const handleNew = () => {
    onNewDashboard?.();
    onClose();
  };

  const deleteDashboardMutation = useMutation({
    mutationFn: ({ dashboardId, headers }) => axios.delete(`/api/dashboards/${dashboardId}`, { headers }),
  });
  const saveDashboardMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/dashboards/save', body, { headers }),
  });

  const handleDelete = async (dashboardId, event) => {
    event.stopPropagation();
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      return;
    }

    const dashboard = dashboards.find(
      (d) => d && (d.id === dashboardId || String(d.id) === String(dashboardId))
    );
    if (isGrantedDashboard(dashboard)) {
      showWarning('No puedes eliminar un dashboard con acceso concedido');
      return;
    }
    
    if (window.confirm('¿Eliminar este dashboard?')) {
      try {
        await deleteDashboardMutation.mutateAsync({ dashboardId, headers: getAuthHeaders() });
        
        onDeleteDashboard(dashboardId);
        showSuccess('Dashboard eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar dashboard:', error);
        showError('Error al eliminar dashboard. Por favor, intenta de nuevo.');
      }
    }
  };

  const handleDuplicate = async (dashboard, event) => {
    event.stopPropagation();
    if (isGrantedDashboard(dashboard)) {
      const { label } = dashboardAccessMeta(dashboard);
      showWarning(
        `No puedes duplicar un dashboard con permiso «${label}». Crea uno nuevo si necesitas una copia editable.`
      );
      return;
    }
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      return;
    }
    const defaultName = `${dashboard.name || 'Dashboard'} (copia)`;
    const newName = window.prompt('Nombre del dashboard duplicado:', defaultName);
    if (!newName?.trim()) return;

    const { id: _omit, updatedAt: _u, ...rest } = dashboard;
    const copy = {
      ...rest,
      name: newName.trim(),
      createdAt: new Date().toISOString(),
    };

    try {
      const res = await saveDashboardMutation.mutateAsync({ body: copy, headers: getAuthHeaders() });
      if (!res.data.success) {
        showError(res.data.error || 'No se pudo duplicar el dashboard');
        return;
      }
      const duplicated = { ...copy, id: res.data.id, publicId: res.data.publicId || null };
      onDashboardDuplicated?.(duplicated);
      onClose();
      showSuccess(`Dashboard «${newName.trim()}» creado`);
    } catch (error) {
      console.error('Error al duplicar dashboard:', error);
      showError(error.response?.data?.error || 'Error al duplicar dashboard. Por favor, intenta de nuevo.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha desconocida';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // Ordenar dashboards por fecha de actualización (más recientes primero)
  const sortedDashboards = [...dashboards].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0);
    const dateB = new Date(b.updatedAt || b.createdAt || 0);
    return dateB - dateA;
  });

  // Filtro de búsqueda por nombre, ID público, ID interno o base de datos
  const term = search.trim().toLowerCase();
  const visibleDashboards = term
    ? sortedDashboards.filter((d) =>
        [d.name, d.publicId, d.id, d.database].some((v) => String(v ?? '').toLowerCase().includes(term))
      )
    : sortedDashboards;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Dashboards Disponibles</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por nombre, ID o base de datos"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
        {visibleDashboards.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            {sortedDashboards.length === 0
              ? 'No hay dashboards guardados. Crea uno nuevo para comenzar.'
              : 'Ningún dashboard coincide con la búsqueda.'}
          </Typography>
        ) : (
          <List>
            {visibleDashboards.map((dashboard) => {
              const readOnly = isDashboardReadOnly(dashboard);
              return (
              <Paper 
                key={dashboard.id} 
                sx={{ 
                  mb: 2, 
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={() => handleLoad(dashboard)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6">{dashboard.name || 'Dashboard sin nombre'}</Typography>
                      <DashboardAccessChip dashboard={dashboard} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Última modificación: {formatDate(dashboard.updatedAt || dashboard.createdAt)}
                    </Typography>
                    {dashboard.publicId && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        ID: {dashboard.publicId}
                      </Typography>
                    )}
                    {dashboard.database && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Base de datos: {dashboard.database}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyDashboardLink?.(dashboard);
                      }}
                      sx={{ mr: 0.5 }}
                      title="Copiar ID"
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDuplicate(dashboard, e)}
                      sx={{ mr: 0.5 }}
                      title="Duplicar dashboard"
                      disabled={readOnly}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLoad(dashboard);
                      }}
                      sx={{ mr: 1 }}
                      title={readOnly ? 'Ver dashboard' : 'Cargar dashboard'}
                    >
                      {readOnly ? <VisibilityIcon /> : <EditIcon />}
                    </IconButton>
                    {!readOnly && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(dashboard.id, e)}
                      color="error"
                      title="Eliminar"
                    >
                      <DeleteIcon />
                    </IconButton>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nuevo dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardList;
